/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import moment from 'moment'
import { connect } from 'react-redux'
import LoadingIndicator from '../LoadingIndicator'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import TableSortLabel from '@mui/material/TableSortLabel'
import NotificationImportantRounded from '@mui/icons-material/NotificationImportantRounded'
import { descendingComparator, getComparator } from '../../../constants/constants'
import { type IWeightSummary, type IWeightData } from '../../../interfaces/weight-summary.interface'
import { type IAlert } from '../../../interfaces/alert.interface'
import { type IChartTableRowProps, type IChartTableHeaderProps, type IChartTableProps } from '../../../interfaces/chart-table.interface'
import { mapProfileDispatch } from '../../../constants/reduxMappers'

function WeightHeader (props: IChartTableHeaderProps): JSX.Element {
  const order = props.order

  const createSortHandler = (property) => (event): void => {
    props.onRequestSort(event, property)
  }

  const renderHeader = (): JSX.Element => {
    return (
      <TableHead>
        <TableRow>
          <TableCell key='date'>
            <TableSortLabel active direction={order} onClick={createSortHandler('date')}>
              Date
            </TableSortLabel>
          </TableCell>

          <TableCell align='center' className='odd-col'>Time</TableCell>
          <TableCell align='center'>Weight</TableCell>
          <TableCell align='center' className='odd-col'>Alert</TableCell>
        </TableRow>
      </TableHead>
    )
  }

  return renderHeader()
}

interface IWeightRowProps extends IChartTableRowProps {
  data: {
    measurements: IWeightData []
  }
  weightUnit: string
}

function WeightRow (props: IWeightRowProps): JSX.Element {
  const dateAsMoment = moment(props.date)
  const formattedDate = moment().isSame(dateAsMoment, 'year') ? dateAsMoment.format('MMM DD') : dateAsMoment.format('MMM DD, YYYY')

  // sort the measurements by timestamp in asc or desc based on date sort
  props.data.measurements.sort((a, b) => props.order === 'desc' ? descendingComparator(a.timestamp, b.timestamp) : -descendingComparator(a.timestamp, b.timestamp))

  const renderRow = (): JSX.Element => {
    return (
      <>
        {props.data?.measurements.map(measurement => {
          return (
            <TableRow sx={{ backgroundColor: theme => `${theme.palette.background.paper}!important` }} key={measurement.timestamp}>
              <TableCell> <Typography display='flex'> {formattedDate} </Typography> </TableCell>
              <TableCell align='center' className='odd-col'>{moment(measurement.timestamp).format('h:mm a')}</TableCell>
              <TableCell align='center'>{measurement.weight.value} {props.weightUnit}</TableCell>
              <TableCell align='center' className='odd-col'>
                {measurement.alerts?.length as number > 0 &&
                  <IconButton aria-label='open alert' size='small' color='primary' onClick={() => props.actions.setSelectedAlert(measurement.alerts?.[0].id)}>
                    <NotificationImportantRounded fontSize='small' />
                  </IconButton>}
              </TableCell>
            </TableRow>
          )
        })}
      </>
    )
  }

  return renderRow()
}

type IGroupedData = Record<string | number, {
  measurements: IWeightData []
}>

function WeightTable (props: IChartTableProps): JSX.Element {
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const handleRequestSort = (): void => { setOrder(order === 'asc' ? 'desc' : 'asc') }

  const groupByDateAndMealStatus = (data: IWeightData [], alerts: IAlert []): IGroupedData => {
    const weightAlerts = alerts.filter(item => item.metric_type === 'weight')
    const groupedData: IGroupedData = {}

    data.forEach((d) => {
      const measurementDate = moment(d.timestamp).format('YYYY-MM-DD')
      if (!groupedData[measurementDate]) groupedData[measurementDate] = { measurements: [] }

      const matchingAlerts = weightAlerts.filter(alert => alert.measurement_timestamp === d.timestamp) ?? []
      const newItem = {
        ...d,
        alerts: matchingAlerts.length ? matchingAlerts : []
      }
      groupedData[measurementDate].measurements.unshift(newItem)
    })
    return groupedData
  }

  const renderTable = (): JSX.Element => {
    if (props.dashboardViewStore.isRangeLoading) {
      return (<div className='metrics-section__inner'> <LoadingIndicator /> </div>)
    }

    const dataSection = props.memberObj.weight as IWeightSummary
    const weightUnit = dataSection?.information.units.weight
    if (!dataSection?.data?.length) {
      return (
        <Box>
          <Typography variant='body1' color='#7f7f7f' marginTop={1.5}>No weight data for this period</Typography>
        </Box>
      )
    }

    const dataByDate = groupByDateAndMealStatus(dataSection.data, props.memberObj.alerts as IAlert [])
    const sectionClass = !props.hideBorder ? 'metrics-section' : ''
    return (
      <TableContainer className={sectionClass} sx={{ height: 408 }}>
        {!props.hideHeader && <h3 className='metrics-section__header'>Weight</h3>}
        <Table className='blood-pressure-table__container' stickyHeader>
          <WeightHeader order={order} onRequestSort={handleRequestSort} />
          <TableBody>
            {Object.keys(dataByDate).slice().sort(getComparator(order)).map((date, i) => {
              return <WeightRow order={order} date={date} data={dataByDate[date]} weightUnit={weightUnit} key={i} actions={props.profileViewActions} />
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return renderTable()
}

const connector = connect(null, mapProfileDispatch)
export default connector(WeightTable)
